import { CheckIcon, PencilIcon, XIcon } from "lucide-react";
import { useState } from "react";

import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";

export interface PullRequestTitleEditorProps {
  readonly title: string;
  readonly editable: boolean;
  readonly pending: boolean;
  readonly onSave: (title: string) => Promise<void>;
}

export function PullRequestTitleEditor(props: PullRequestTitleEditorProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(props.title);
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    const title = draft.trim();
    if (title.length === 0 || title === props.title) {
      setEditing(false);
      return;
    }
    setError(null);
    try {
      await props.onSave(title);
      setEditing(false);
    } catch {
      setError("Couldn’t save the title. Your draft is still here—try again.");
    }
  };
  if (!editing) {
    return (
      <div className="group flex min-w-0 items-start gap-1">
        <h2 className="min-w-0 flex-1 truncate text-base font-semibold">{props.title}</h2>
        {props.editable ? (
          <Button
            type="button"
            size="icon-xs"
            variant="ghost"
            aria-label="Edit pull request title"
            disabled={props.pending}
            className="shrink-0 opacity-100 md:opacity-0 md:group-hover:opacity-100"
            onClick={() => {
              setDraft(props.title);
              setError(null);
              setEditing(true);
            }}
          >
            <PencilIcon className="size-3.5" />
          </Button>
        ) : null}
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5">
        <Input
          aria-label="Pull request title"
          value={draft}
          autoFocus
          maxLength={1024}
          disabled={props.pending}
          onChange={(event) => setDraft(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") void save();
            if (event.key === "Escape") setEditing(false);
          }}
        />
        <Button
          type="button"
          size="icon-xs"
          aria-label="Save pull request title"
          disabled={props.pending}
          onClick={() => void save()}
        >
          <CheckIcon className="size-3.5" />
        </Button>
        <Button
          type="button"
          size="icon-xs"
          variant="ghost"
          aria-label="Cancel title edit"
          disabled={props.pending}
          onClick={() => setEditing(false)}
        >
          <XIcon className="size-3.5" />
        </Button>
      </div>
      {error ? (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}
